import { useState, useEffect } from 'react'
import { Loader2, CheckCircle, XCircle } from 'lucide-react'
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@/shared/components/ui/sheet'
import { Button } from '@/shared/components/ui/button'
import { Input } from '@/shared/components/ui/input'
import { Label } from '@/shared/components/ui/label'
import { Switch } from '@/shared/components/ui/switch'
import { Badge } from '@/shared/components/ui/badge'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/components/ui/select'
import { Separator } from '@/shared/components/ui/separator'
import { useCreateFuenteCEMC, useUpdateFuenteCEMC } from '../hooks/useCEMC'
import { cemcApi } from '../api/cemc.api'
import type { FuenteDatosCEMC } from '../types'

const DB_ENGINES = [
  { value: 'mysql', label: 'MySQL', port: '3306' },
  { value: 'postgresql', label: 'PostgreSQL', port: '5432' },
  { value: 'mssql', label: 'SQL Server', port: '1433' },
]

const ROLES = ['registros', 'neuroventas', 'liderazgo', 'classroom', 'webinar', 'otro']

interface Props {
  open: boolean
  onClose: () => void
  configId: number
  editing?: FuenteDatosCEMC | null
}

export function FuenteFormSheet({ open, onClose, configId, editing }: Props) {
  const [nombre, setNombre] = useState('')
  const [tipo, setTipo] = useState<'bd' | 'archivo' | 'google_sheets'>('bd')
  const [rol, setRol] = useState('')
  const [orden, setOrden] = useState(0)
  const [activo, setActivo] = useState(true)
  // BD fields
  const [dbHost, setDbHost] = useState('')
  const [dbPort, setDbPort] = useState('3306')
  const [dbName, setDbName] = useState('')
  const [dbUser, setDbUser] = useState('')
  const [dbPassword, setDbPassword] = useState('')
  const [dbEngine, setDbEngine] = useState('mysql')
  const [tablaNombre, setTablaNombre] = useState('')
  // Google fields
  const [googleSheetUrl, setGoogleSheetUrl] = useState('')
  const [googleCredentialsJson, setGoogleCredentialsJson] = useState('')
  // Test state (shared between BD and Google)
  const [testResult, setTestResult] = useState<{ success: boolean; message: string; columnas?: string[] } | null>(null)
  const [testLoading, setTestLoading] = useState(false)

  const createMutation = useCreateFuenteCEMC()
  const updateMutation = useUpdateFuenteCEMC()
  const isLoading = createMutation.isPending || updateMutation.isPending

  useEffect(() => {
    if (editing) {
      setNombre(editing.nombre)
      setTipo(editing.tipo)
      setRol(editing.rol)
      setOrden(editing.orden)
      setActivo(editing.activo)
      setDbHost(editing.db_host)
      setDbPort(editing.db_port)
      setDbName(editing.db_name)
      setDbUser(editing.db_user ?? '')
      setDbPassword('')
      setDbEngine(editing.db_engine)
      setTablaNombre(editing.tabla_nombre)
      setGoogleSheetUrl(editing.google_sheet_url ?? '')
      setGoogleCredentialsJson('')
    } else {
      setNombre('')
      setTipo('bd')
      setRol('')
      setOrden(0)
      setActivo(true)
      setDbHost('')
      setDbPort('3306')
      setDbName('')
      setDbUser('')
      setDbPassword('')
      setDbEngine('mysql')
      setTablaNombre('')
      setGoogleSheetUrl('')
      setGoogleCredentialsJson('')
    }
    setTestResult(null)
  }, [editing, open])

  const handleEngineChange = (engine: string) => {
    setDbEngine(engine)
    const found = DB_ENGINES.find((e) => e.value === engine)
    if (found) setDbPort(found.port)
  }

  const handleTest = async () => {
    setTestLoading(true)
    setTestResult(null)
    try {
      const res = await cemcApi.testConexion({
        db_host: dbHost, db_port: dbPort, db_name: dbName,
        db_user: dbUser, db_password: dbPassword, db_engine: dbEngine,
        tabla_nombre: tablaNombre,
      })
      setTestResult(res)
    } catch {
      setTestResult({ success: false, message: 'Error al probar conexión' })
    } finally {
      setTestLoading(false)
    }
  }

  const handleTestGoogle = async () => {
    setTestLoading(true)
    setTestResult(null)
    try {
      const res = await cemcApi.testGoogleSheets({
        google_sheet_url: googleSheetUrl,
        google_credentials_json: googleCredentialsJson || undefined,
      })
      setTestResult(res)
    } catch {
      setTestResult({ success: false, message: 'Error al probar acceso' })
    } finally {
      setTestLoading(false)
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    const payload = {
      nombre, tipo, rol, orden, activo,
      db_host: dbHost, db_port: dbPort, db_name: dbName,
      db_user: dbUser, db_password: dbPassword, db_engine: dbEngine,
      tabla_nombre: tablaNombre,
      google_sheet_url: googleSheetUrl,
      google_credentials_json: googleCredentialsJson,
    }
    try {
      if (editing) {
        await updateMutation.mutateAsync({ id: editing.id, payload })
      } else {
        await createMutation.mutateAsync({ configId, payload })
      }
      onClose()
    } catch {}
  }

  const TestFeedback = () => testResult ? (
    <div className="flex items-center gap-1">
      {testResult.success
        ? <CheckCircle className="h-4 w-4 text-green-600" />
        : <XCircle className="h-4 w-4 text-red-600" />}
      <span className={`text-xs ${testResult.success ? 'text-green-700' : 'text-red-700'}`}>
        {testResult.message}
      </span>
    </div>
  ) : null

  return (
    <Sheet open={open} onOpenChange={onClose}>
      <SheetContent className="w-full sm:max-w-lg overflow-y-auto">
        <SheetHeader>
          <SheetTitle>{editing ? 'Editar Fuente' : 'Nueva Fuente de Datos'}</SheetTitle>
        </SheetHeader>
        <form onSubmit={handleSubmit} className="space-y-4 mt-4">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label>Nombre</Label>
              <Input value={nombre} onChange={(e) => setNombre(e.target.value)} required />
            </div>
            <div className="space-y-2">
              <Label>Tipo</Label>
              <Select value={tipo} onValueChange={(v) => { setTipo(v as typeof tipo); setTestResult(null) }}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="bd">Base de Datos</SelectItem>
                  <SelectItem value="archivo">Archivo CSV/XLSX</SelectItem>
                  <SelectItem value="google_sheets">Google Sheets / Drive</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label>Rol semántico</Label>
              <Select value={rol} onValueChange={setRol}>
                <SelectTrigger><SelectValue placeholder="Seleccionar rol" /></SelectTrigger>
                <SelectContent>
                  {ROLES.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Orden</Label>
              <Input type="number" value={orden} onChange={(e) => setOrden(Number(e.target.value))} />
            </div>
          </div>

          {tipo === 'bd' && (
            <>
              <Separator />
              <div className="space-y-2">
                <Label>Motor</Label>
                <Select value={dbEngine} onValueChange={handleEngineChange}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {DB_ENGINES.map((e) => <SelectItem key={e.value} value={e.value}>{e.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div className="col-span-2 space-y-2">
                  <Label>Host</Label>
                  <Input value={dbHost} onChange={(e) => setDbHost(e.target.value)} placeholder="host o IP" />
                </div>
                <div className="space-y-2">
                  <Label>Puerto</Label>
                  <Input value={dbPort} onChange={(e) => setDbPort(e.target.value)} />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Base de datos</Label>
                <Input value={dbName} onChange={(e) => setDbName(e.target.value)} />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label>Usuario</Label>
                  <Input value={dbUser} onChange={(e) => setDbUser(e.target.value)} />
                </div>
                <div className="space-y-2">
                  <Label>Contraseña</Label>
                  <Input type="password" value={dbPassword} onChange={(e) => setDbPassword(e.target.value)} />
                </div>
              </div>
              {editing && editing.db_password === '***' && !dbPassword && (
                <p className="text-xs text-amber-600">
                  Contraseña guardada. Déjala vacía para mantenerla, o escríbela de nuevo para reemplazarla o probar la conexión.
                </p>
              )}
              <div className="space-y-2">
                <Label>Tabla</Label>
                <Input value={tablaNombre} onChange={(e) => setTablaNombre(e.target.value)} placeholder="nombre_tabla" />
              </div>
              <div className="flex items-center gap-2">
                <Button type="button" variant="outline" size="sm" onClick={handleTest} disabled={testLoading}>
                  {testLoading && <Loader2 className="mr-2 h-3 w-3 animate-spin" />}
                  Probar conexión
                </Button>
                <TestFeedback />
              </div>
              {testResult?.columnas?.length ? (
                <div className="flex flex-wrap gap-1">
                  {testResult.columnas.map((c) => (
                    <Badge key={c} variant="secondary" className="text-xs">{c}</Badge>
                  ))}
                </div>
              ) : null}
            </>
          )}

          {tipo === 'google_sheets' && (
            <>
              <Separator />
              <div className="space-y-2">
                <Label>URL de Google Sheets o Drive</Label>
                <Input
                  value={googleSheetUrl}
                  onChange={(e) => setGoogleSheetUrl(e.target.value)}
                  placeholder="https://docs.google.com/spreadsheets/d/..."
                  required={tipo === 'google_sheets'}
                />
                <p className="text-xs text-muted-foreground">
                  Pega el enlace del Sheet o archivo XLSX en Drive. Para acceso público, comparte con "Cualquier usuario con el enlace → Ver".
                </p>
              </div>
              <div className="space-y-2">
                <Label>
                  Credenciales de Cuenta de Servicio{' '}
                  <span className="text-muted-foreground font-normal">(opcional — solo para archivos privados)</span>
                </Label>
                <textarea
                  className="w-full border rounded-md px-3 py-2 text-xs font-mono h-28 resize-none bg-background"
                  value={googleCredentialsJson}
                  onChange={(e) => setGoogleCredentialsJson(e.target.value)}
                  placeholder={'{ "type": "service_account", "project_id": "...", ... }'}
                />
                {editing?.google_credentials_json === '***' && !googleCredentialsJson && (
                  <p className="text-xs text-amber-600">Credenciales guardadas. Deja vacío para mantenerlas o pega nuevas para reemplazarlas.</p>
                )}
              </div>
              <div className="flex items-center gap-2">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={handleTestGoogle}
                  disabled={testLoading || !googleSheetUrl}
                >
                  {testLoading && <Loader2 className="mr-2 h-3 w-3 animate-spin" />}
                  Probar acceso
                </Button>
                <TestFeedback />
              </div>
              {testResult?.columnas?.length ? (
                <div className="flex flex-wrap gap-1">
                  {testResult.columnas.map((c) => (
                    <Badge key={c} variant="secondary" className="text-xs">{c}</Badge>
                  ))}
                </div>
              ) : null}
            </>
          )}

          <div className="flex items-center gap-2">
            <Switch checked={activo} onCheckedChange={setActivo} id="fuente-activo" />
            <Label htmlFor="fuente-activo">Activo</Label>
          </div>
          <div className="flex gap-2 pt-2">
            <Button type="submit" disabled={isLoading} className="flex-1">
              {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {editing ? 'Guardar cambios' : 'Crear fuente'}
            </Button>
            <Button type="button" variant="outline" onClick={onClose}>Cancelar</Button>
          </div>
        </form>
      </SheetContent>
    </Sheet>
  )
}
